import React, { useState, useEffect } from 'react';
import { 
  Button,
  SpaceBetween,
  Modal,
  Box,
  StatusIndicator
} from '@cloudscape-design/components';
import { BaseTable } from './BaseTable';
import { useLocalStorage } from '../commons/use-local-storage';
import { FullPageHeader } from '../commons';

import { submissionsApi } from 'api/Submission';

const AccountStatus = {
  ACTIVE: 'active',
  INACTIVE: 'inactive'
};

// Status component for consistent styling
const StatusComponent = ({ status }) => {

  switch (status) {
    case AccountStatus.ACTIVE:
      return <StatusIndicator type="success">Active</StatusIndicator>;
    case AccountStatus.INACTIVE:
      return <StatusIndicator type="stopped">Inactive</StatusIndicator>;
    default:
      return <StatusIndicator type="pending">Unknown</StatusIndicator>;
  }
};

const defaultStatus = { value: '0', label: 'All Status' };

const COLUMN_DEFINITIONS = [
  {
    id: 'id',
    header: 'ID',
    cell: item => item.id,
  },
  {
    id: 'target_account_id',
    header: 'Account ID',
    cell: item => item.target_account_id,
  },
  {
    id: 'account_name',
    header: 'Account Name',
    cell: item => item.account_name,
  },
  {
    id: 'create_datetime',
    header: 'Created',
    cell: item => new Date(item.create_datetime).toLocaleString(),
  },
  {
    id: 'status',
    header: 'Status',
    cell: item => <StatusComponent status={item.is_active ? AccountStatus.ACTIVE : AccountStatus.INACTIVE} />,
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: item => {
      return (
        <span>
          <Button 
            variant="link" 
            onClick={() => {window.location.href = `/external-accounts/edit/${item.id}`}}
          >
            Edit
          </Button>
          {/* Other action buttons */}
        </span>
      );
    }
  }
];

const STATUS_OPTIONS = [
  defaultStatus,
  { label: 'Active', value: '1' },
  { label: 'Inactive', value: '2' }
];

export function ExternalAccountsTable() {
  const [data, setData] = useState([]); 
  const [statusFilter, setStatusFilter] = useState(defaultStatus);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadMoreIsVisible, setLoadMoreIsVisible] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isActivateModalVisible, setActivateModalVisible] = useState(false);
  const [isDeactivateModalVisible, setDeactivateModalVisible] = useState(false);

  const [preferences, setPreferences] = useLocalStorage('External-Accounts-Table-Preferences', {
    pageSize: 10,
    wrapLines: false,
    stripedRows: false,
    contentDensity: 'comfortable',
  });

  const fetchData = async (isRefresh=false) => {
    const isActualRefresh = typeof isRefresh === 'boolean' ? isRefresh : false;
    
    try {
      const params = {
        limit: 500  // Always fetch 500 items from API
      };
  
      if (lastEvaluatedKey && !isActualRefresh) {
        params.last_evaluated_key = lastEvaluatedKey;
      }
  
      if (statusFilter.value !== '0') {
        params.is_active = statusFilter.label === 'Active';
      }
  
      const result = await submissionsApi.getExternalAccountsPaginated(params);
  
      if (lastEvaluatedKey && !isActualRefresh) {
        setData(prev => [...prev, ...result.items]);
      } else {
        setData(result.items);
      }

      setLoadMoreIsVisible(result.last_evaluated_key !== null);
      setLastEvaluatedKey(result.last_evaluated_key);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshTable = () => {
    setIsLoading(true);
    setLastEvaluatedKey(null);
    setData([]);
    setLoadMoreIsVisible(true);
    setSelectedItems([]);
    fetchData(true);
  }
  useEffect(() => {
    refreshTable();
  }, [statusFilter]);  // Removed preferences.pageSize from dependencies

  const handleStatusChange = async (isActive) => {
    try {
      const selectedItem = selectedItems[0];

      await submissionsApi.updateExternalAccountStatus({
        id: selectedItem.id,
        is_active: isActive
      });

      setActivateModalVisible(false);
      setDeactivateModalVisible(false);
      
      refreshTable();
    }
    catch (error) {
      console.error('Error updating account status:', error);
    }
  };

  const tableHeader = (
    <FullPageHeader
      title="External Accounts"
      selectedItemsCount={selectedItems.length}
      actions={
        <SpaceBetween size="xs" direction="horizontal">
          <Button 
            variant="primary" 
            onClick={() => setActivateModalVisible(true)}
            disabled={!selectedItems.length || selectedItems[0]?.is_active}
          >
            Activate
          </Button>
          <Button 
            variant="primary" 
            onClick={() => setDeactivateModalVisible(true)}
            disabled={!selectedItems.length || !selectedItems[0]?.is_active}
          >
            Deactivate
          </Button>
        </SpaceBetween>
      }
    />
  );

  return (
    <>
      <BaseTable
        columnDefinitions={COLUMN_DEFINITIONS}
        items={data}
        loading={isLoading}
        statusOptions={STATUS_OPTIONS}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        onFetchData={fetchData}
        preferences={preferences}
        setPreferences={setPreferences}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        loadMoreIsVisible={loadMoreIsVisible}
        tableHeader={tableHeader}
      />

      {/* Action Modals */}
      <Modal
        visible={isActivateModalVisible}
        onDismiss={() => setActivateModalVisible(false)}
        header="Activate Account"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setActivateModalVisible(false)}>Cancel</Button>
              <Button variant="primary" onClick={() => handleStatusChange(true)}>Activate</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <p>Are you sure you want to activate this account?</p>
      </Modal>

      <Modal
        visible={isDeactivateModalVisible}
        onDismiss={() => setDeactivateModalVisible(false)}
        header="Deactivate Account"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setDeactivateModalVisible(false)}>Cancel</Button>
              <Button variant="primary" onClick={() => handleStatusChange(false)}>Deactivate</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <p>Are you sure you want to deactivate this account?</p>
      </Modal>
    </>
  );
}

export default ExternalAccountsTable;